<script setup lang="ts">
import lodashIsNumber from 'lodash-es/isNumber';
import type { PropType } from 'vue';

import { SellingMethod } from '~/apiClient';
import { type SaleItemEntity, isAuction } from '~/types/sale.type';

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
});

// COMPOSABLES
const { tn } = useTypedI18n();
const { title, titleColor, titleAnnotation, titleAnnotationColor, priceColor, isSeller, isFeeNoticeHidden } = isAuction(sale)
    ? useAuctionStatus(toRef(() => sale))
    : useDirectSaleStatus(toRef(() => sale));
const price = computed(() => (sale?.sellingMethod === SellingMethod.Auction ? sale.currentPrice : sale.price));
</script>

<template>
    <!-- Desktop -->
    <div class="hidden lg:flex lg:flex-col lg:gap-4">
        <div class="flex items-center justify-between">
            <p class="flex gap-3 text-sm font-extrabold tracking-widest uppercase lg:max-xl:flex-col">
                <span :class="titleColor">{{ title }}</span>
                <span v-if="titleAnnotation" :class="titleAnnotationColor">{{ titleAnnotation }}</span>
            </p>
        </div>

        <template v-if="lodashIsNumber(price)">
            <p class="text-5xl font-semibold" :class="priceColor">{{ tn(price, 'currency') }}</p>
            <SaleFees
                v-if="!isFeeNoticeHidden"
                :reference-date="sale.sellingMethod === SellingMethod.Auction ? sale.startAt : undefined"
                :is-buyer="!isSeller"
                :base-amount="price"
                :assets-count="sale.assets.length"
                :fee-configuration="sale.userMeta?.feeOverride"
                wrapper-class="text-left"
            />
        </template>
        <p v-else class="text-5xl font-semibold uppercase" :class="priceColor">*******</p>

        <template v-if="isAuction(sale)">
            <AdpCountdown class="text-base" :auction="sale" />
            <AuctionReservePriceNotice :auction="sale" class="self-start" />
        </template>
    </div>

    <!-- Mobile/Tablet -->
    <div class="flex flex-col gap-3 lg:hidden">
        <div class="flex items-center justify-between">
            <p class="flex gap-3 text-xs font-extrabold tracking-widest uppercase">
                <span :class="titleColor">{{ title }}</span>
                <span v-if="titleAnnotation" :class="titleAnnotationColor">{{ titleAnnotation }}</span>
            </p>
        </div>

        <div class="flex flex-col gap-0">
            <div class="flex items-center justify-between">
                <div>
                    <template v-if="lodashIsNumber(price)">
                        <p class="text-2xl font-semibold" :class="priceColor">{{ tn(price, 'currency') }}</p>
                        <SaleFees
                            v-if="!isFeeNoticeHidden"
                            :reference-date="sale.sellingMethod === SellingMethod.Auction ? sale.startAt : undefined"
                            :is-buyer="!isSeller"
                            :base-amount="price"
                            :assets-count="sale.assets.length"
                            :fee-configuration="sale.userMeta?.feeOverride"
                            wrapper-class="text-left hidden text-xs sm:block"
                        />
                    </template>
                    <p v-else class="text-2xl font-semibold uppercase" :class="priceColor">*******</p>
                </div>

                <AdpCountdown v-if="isAuction(sale)" class="flex flex-col items-center text-sm" :auction="sale" />
            </div>
            <SaleFees
                v-if="!isFeeNoticeHidden && !!price"
                :reference-date="sale.sellingMethod === SellingMethod.Auction ? sale.startAt : undefined"
                :is-buyer="!isSeller"
                :base-amount="price"
                :assets-count="sale.assets.length"
                :fee-configuration="sale.userMeta?.feeOverride"
                wrapper-class="text-left sm:hidden"
            />
        </div>

        <AuctionReservePriceNotice v-if="isAuction(sale)" :auction="sale" class="self-start" />
    </div>
</template>
